import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import firebase from "firebase"
import moment from "moment"
import random from "randomstring"
import { Check, Copy, Trash2, Upload } from "lucide-react"

import { AlertsContext } from "../../../../utils/providers/alerts"
import { AuthContext } from "../../../../utils/providers/auth"
import { ModalContext } from "../../../../utils/providers/modal"
import { db, funEU, storage } from "../../../../utils/firebase"
import {
	getCommunicationBodyUrl,
	loadCommunicationBody,
} from "../../../../utils/communications/load-body"
import { LoadingIcon } from "../../../../utils/svgs"
import Button from "../../../ui/button/button"
import Table from "../../../structure/table/table"
import Window from "../../../structure/window/window"
import Badge from "../../../ui/badge/badge"
import Timeline from "../../../ui/timeline/timeline"
import ProcessModal from "./process-modal"
import { buildSupplierTimeline } from "./process-timeline"
import "./contracts-rates.scss"

const ACCEPTED_FILE_TYPES = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"text/csv",
	"text/plain",
	"image/jpeg",
	"image/png",
	"image/webp",
]

const ACCEPTED_FILE_TYPES_ATTR = ACCEPTED_FILE_TYPES.join(",")

const kindPath = (supplierKind) => supplierKind === "CMS_COURSE" ? "cms_courses" : "cms_holidays"

const cleanFilename = (filename) => {
	return (filename || "file")
		.normalize("NFKD")
		.replace(/[^\w.\-]+/g, "_")
		.replace(/_+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 160) || "file"
}

const hashFile = async (file) => {
	if (!window.crypto?.subtle) return null
	const bytes = await file.arrayBuffer()
	const digest = await window.crypto.subtle.digest("SHA-256", bytes)
	return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("")
}

const statusBadgeType = (status) => {
	if (status === "APPROVED") return "POSITIVE"
	if (status === "FAILED" || status === "FAILED_UNROUTED") return "NEGATIVE"
	if (status === "REVIEW_REQUIRED") return "AMBER"
	// UPLOADED / INGESTED → grey default badge — both mean "waiting for the user
	// to trigger a parse from the row's button."
	if (status === "UPLOADED" || status === "INGESTED") return null
	return "INFO"
}

const statusLabel = (status) => {
	if (status === "UPLOADED" || status === "INGESTED") return "Pending parsing"
	const text = (status || "UPLOADED").replace(/_/g, " ").toLowerCase()
	return text.charAt(0).toUpperCase() + text.slice(1)
}

const formatFileSize = (bytes) => {
	if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return null
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const SORT_STORAGE_KEY = "contractsRates.sort"

const readStoredSort = () => {
	try {
		const raw = window.localStorage.getItem(SORT_STORAGE_KEY)
		if (!raw) return { key: "", direction: "" }
		const parsed = JSON.parse(raw)
		if (parsed && typeof parsed.key === "string" && typeof parsed.direction === "string") {
			return parsed
		}
	} catch {
		// ignore parse/storage errors and fall back to default
	}
	return { key: "", direction: "" }
}

const writeStoredSort = (value) => {
	try {
		window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(value))
	} catch {
		// ignore storage errors
	}
}

const nextSortState = (currentSort, key) => {
	if (currentSort.key !== key) return { key, direction: "asc" }
	if (currentSort.direction === "asc") return { key, direction: "desc" }
	return { key: "", direction: "" }
}

const getSeconds = (timestamp) => timestamp?._seconds || timestamp?.seconds || 0

const compareSortValues = (a, b) => {
	const aMissing = a === null || a === undefined || a === ""
	const bMissing = b === null || b === undefined || b === ""
	// Missing values always sink to the end regardless of sort direction so an
	// asc sort doesn't bury filled rows under a stack of unfilled ones.
	if (aMissing && bMissing) return 0
	if (aMissing) return 1
	if (bMissing) return -1
	if (typeof a === "number" || typeof b === "number") return (a || 0) - (b || 0)
	return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
}

const getSortValue = (doc, key) => {
	switch (key) {
		case "ingested":
			return getSeconds(doc.created_at)
		case "validity":
			// `effective_from` is an ISO YYYY-MM-DD string — string-compare is
			// correct because ISO dates are lexicographically sortable.
			return doc.effective_from || ""
		default:
			return ""
	}
}

const truncate = (text, max = 100) => {
	if (!text) return text
	const value = String(text)
	return value.length > max ? `${value.slice(0, max)}…` : value
}

const formatValidity = (from, to) => {
	if (!from && !to) return null
	const fmt = (value) => moment(value).format("DD MMM YYYY")
	if (from && to) return `${fmt(from)} → ${fmt(to)}`
	if (from) return `from ${fmt(from)}`
	return `until ${fmt(to)}`
}

// The schema definition and agent parameters consumed by the modals live in
// src/utils/agent-parameters/supplier-documents/{schema,parameters}.js — those
// files are the single hard-coded source of truth.

export default function ContractsRates({ supplierKind, supplierId, supplierTitle, supplier = {} }) {
	const { user } = useContext(AuthContext)
	const { pushAlert } = useContext(AlertsContext)
	const { showModal } = useContext(ModalContext)
	const [documents, setDocuments] = useState([])
	const [loading, setLoading] = useState(true)
	const [route, setRoute] = useState({
		email_address: supplier.contracts_ingest_address || "",
		token: supplier.contracts_ingest_route_token || "",
	})
	const [uploading, setUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState("")
	const [isDragOver, setIsDragOver] = useState(false)
	const [selected, setSelected] = useState(null)
	const [copied, setCopied] = useState(false)
	const [processingDoc, setProcessingDoc] = useState(null)
	const [sort, setSort] = useState(() => readStoredSort())
	const fileInputRef = useRef(null)

	useEffect(() => {
		writeStoredSort(sort)
	}, [sort])

	useEffect(() => {
		if (!supplierKind || !supplierId) return
		setLoading(true)
		const unsubscribe = db
			.collection("supplier_contract_documents")
			.where("supplier_kind", "==", supplierKind)
			.where("supplier_id", "==", supplierId)
			.orderBy("created_at", "desc")
			.onSnapshot(
				(snapshot) => {
					setDocuments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
					setLoading(false)
				},
				(err) => {
					console.error("Failed to load supplier contract documents", err)
					setLoading(false)
				}
			)

		return () => unsubscribe()
	}, [supplierKind, supplierId])

	const ensureRoute = useCallback(async () => {
		try {
			const callable = funEU.httpsCallable("ensureSupplierIngestRoute")
			const response = await callable({
				supplier_kind: supplierKind,
				supplier_id: supplierId,
			})
			setRoute(response.data || {})
		} catch (err) {
			console.error("Failed to create supplier ingest route", err)
		}
	}, [supplierKind, supplierId])

	useEffect(() => {
		if (!route.email_address && supplierId && supplierKind) {
			ensureRoute()
		}
	}, [supplierId, supplierKind, route.email_address, ensureRoute])

	const copyAddress = async () => {
		if (!route.email_address) return
		await navigator.clipboard.writeText(route.email_address)
		setCopied(true)
		pushAlert({
			type: "SUCCESS",
			title: "Forwarding address copied",
		})
		window.setTimeout(() => setCopied(false), 1500)
	}

	const uploadFiles = async (files) => {
		const fileList = Array.from(files || [])
		if (fileList.length === 0) return

		setUploading(true)
		try {
			for (let index = 0; index < fileList.length; index++) {
				const file = fileList[index]
				setUploadProgress(`Uploading ${index + 1} of ${fileList.length}: ${file.name}`)

				const docRef = db.collection("supplier_contract_documents").doc()
				const uploadId = random.generate(8)
				const filename = cleanFilename(file.name)
				const storagePath = `suppliers/${kindPath(supplierKind)}/${supplierId}/contracts/${docRef.id}/original/${uploadId}-${filename}`
				const checksum = await hashFile(file)

				const uploadSnapshot = await storage.ref(storagePath).put(file, {
					contentType: file.type || "application/octet-stream",
					customMetadata: {
						supplierKind,
						supplierId,
						documentId: docRef.id,
						uploadedBy: user?.uid || "",
					},
				})
				const downloadUrl = await uploadSnapshot.ref.getDownloadURL()

				await docRef.set({
					supplier_kind: supplierKind,
					supplier_id: supplierId,
					source: "MANUAL_UPLOAD",
					status: "UPLOADED",
					storage_path: storagePath,
					download_url: downloadUrl,
					filename: file.name,
					content_type: file.type || "application/octet-stream",
					size: file.size,
					checksum,
					classification: null,
					classification_confidence: null,
					date_ranges: [],
					created_at: firebase.firestore.FieldValue.serverTimestamp(),
					updated_at: firebase.firestore.FieldValue.serverTimestamp(),
					uploaded_by: user?.uid || null,
				})
			}
		} finally {
			setUploadProgress("")
			setUploading(false)
		}
	}

	const handleDrop = (event) => {
		event.preventDefault()
		setIsDragOver(false)

		const dropped = Array.from(event.dataTransfer?.files || [])
		const accepted = dropped.filter((file) => {
			if (ACCEPTED_FILE_TYPES.includes(file.type)) return true
			return /\.(pdf|docx?|xlsx?|csv|txt|jpe?g|png|webp)$/i.test(file.name)
		})

		if (accepted.length === 0) return
		uploadFiles(accepted)
	}

	const handleDragOver = (event) => {
		event.preventDefault()
		setIsDragOver(true)
	}

	const handleDragLeave = (event) => {
		event.preventDefault()
		setIsDragOver(false)
	}

	const handleBrowseClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileInputChange = (event) => {
		uploadFiles(event.target.files)
		event.target.value = ""
	}

	const confirmDelete = (doc) => {
		showModal({
			type: "ALERT",
			title: "Are you sure?",
			body: "Are you sure you'd like to delete this ingestion? This cannot be undone.",
			cancel: {
				label: "Cancel",
				action: () => null,
			},
			next: {
				label: "Yes, delete",
				action: async () => {
					try {
						// Storage objects (body, attachments) are intentionally not removed
						// here — they are shared with the parent communications/{id} doc
						// and any sibling supplier rows from the same email.
						await db.doc(`supplier_contract_documents/${doc.id}`).delete()
					} catch (err) {
						pushAlert({
							type: "ERROR",
							title: "Failed to delete ingestion",
							body: err?.message || String(err),
						})
					}
				},
			},
		})
	}

	const rows = useMemo(() => {
		if (!sort.key || !sort.direction) return documents
		return [...documents].sort((a, b) => {
			const comparison = compareSortValues(getSortValue(a, sort.key), getSortValue(b, sort.key))
			return sort.direction === "asc" ? comparison : -comparison
		})
	}, [documents, sort])

	const headings = useMemo(
		() =>
			[
				{ key: "ingested", label: "Ingested", sortable: true },
				{ key: "last_parsed", label: "Last parsed" },
				{ key: "document", label: "Document" },
				{ key: "supplier", label: "Supplier" },
				{ key: "referenced", label: "Referenced entities" },
				{ key: "classification", label: "Classification" },
				{ key: "validity", label: "Validity", sortable: true },
				{ key: "status", label: "Status" },
				{ key: "actions", label: "" },
			].map((heading) => ({
				...heading,
				active: !!heading.sortable && sort.key === heading.key,
				direction:
					heading.sortable && sort.key === heading.key ? sort.direction : "",
				onClick: heading.sortable
					? () => setSort((current) => nextSortState(current, heading.key))
					: undefined,
			})),
		[sort]
	)

	const stats = useMemo(() => {
		const total = documents.length
		const lastIngestionSeconds = documents[0]?.created_at?.seconds
		const lastIngestion = lastIngestionSeconds ? moment.unix(lastIngestionSeconds) : null
		const awaitingReview = documents.filter(
			(doc) => doc.status === "REVIEW_REQUIRED"
		).length
		return {
			total,
			lastIngestionFormatted: lastIngestion?.format("DD MMM YYYY HH:mm") || null,
			lastIngestionRelative: lastIngestion?.fromNow() || null,
			awaitingReview,
		}
	}, [documents])

	return (
		<div className="supplier-contracts-rates">
			<div className="supplier-contracts-rates-grid">
				<div
					className={[
						"supplier-contracts-rates-card",
						"is-upload",
						"supplier-contracts-rates-dropzone",
						isDragOver && "is-drag-over",
						uploading && "is-uploading",
					].filter(Boolean).join(" ")}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onClick={handleBrowseClick}
					role="button"
					tabIndex={0}>
					<Upload size={20} color="#667085" />
					<div className="supplier-contracts-rates-dropzone-text">
						<strong>
							{uploading ? "Uploading..." : "Drag & drop documents here"}
						</strong>
						<small>
							{uploading
								? uploadProgress
								: "or click to browse — PDF, Word, Excel/CSV, images, text"}
						</small>
					</div>
					<input
						ref={fileInputRef}
						type="file"
						multiple
						accept={ACCEPTED_FILE_TYPES_ATTR}
						onChange={handleFileInputChange}
						hidden
					/>
				</div>

				<div className="supplier-contracts-rates-card is-stats">
					<div className="supplier-contracts-rates-stats">
						<div className="row">
							<span className="label">Total ingestions</span>
							<span className="value">{stats.total}</span>
						</div>
						<div className="row">
							<span className="label">Last ingestion</span>
							<span className="value">
								{stats.lastIngestionFormatted || "—"}
								{stats.lastIngestionRelative && (
									<span className="relative"> ({stats.lastIngestionRelative})</span>
								)}
							</span>
						</div>
						<div className="row">
							<span className="label">Awaiting review</span>
							<span className="value">{stats.awaitingReview}</span>
						</div>
					</div>
				</div>

				<div className="supplier-contracts-rates-card is-forward">
					<label className="supplier-contracts-rates-label">Forwarding address</label>

					<div className="supplier-contracts-rates-address">
						<code>
							<span>{route.email_address || "Creating forwarding address..."}</span>
						</code>
						<Button
							small={true}
							icon={copied ? <Check size={14} /> : <Copy size={14} />}
							disabled={!route.email_address}
							onClick={() => copyAddress()}
						/>
					</div>
				</div>
			</div>

			<Table
				className="supplier-contracts-rates-table"
				headings={headings}
				noResults={!loading && rows.length === 0}
				noResultsMessage="No contracts or rate documents yet">
				{rows.map((document) => {
					const classificationKey = document.classification
					const validity = formatValidity(document.effective_from, document.effective_to)
					const confidence =
						document.agent_metadata?.confidence_score ?? document.classification_confidence
					const supplierName = document.supplier_details?.formatted_name
					const referencedEntities = [
						...(document.referenced_hotels || []),
						...(document.referenced_courses || []),
					]

					return (
						<tr key={document.id}>
							<td>
								{document.created_at?.seconds
									? moment.unix(document.created_at.seconds).format("DD MMM YYYY HH:mm")
									: "Processing..."}
							</td>
							<td>
								{document.parsed_at?.seconds ? (
									moment.unix(document.parsed_at.seconds).format("DD MMM YYYY HH:mm")
								) : (
									<span className="supplier-contracts-rates-muted">—</span>
								)}
							</td>
							<td>
								{(() => {
									const titleText =
										document.filename || document.email_subject || "Forwarded email"
									return (
										<strong title={titleText}>{truncate(titleText, 100)}</strong>
									)
								})()}
								{document.source === "EMAIL_FORWARD" &&
									document.filename &&
									document.email_subject && (
										<small
											className="supplier-contracts-rates-from-email"
											title={document.email_subject}>
											from email: {truncate(document.email_subject, 100)}
										</small>
									)}
							</td>
							<td>
								{supplierName || <span className="supplier-contracts-rates-muted">—</span>}
							</td>
							<td>
								{referencedEntities.length > 0 ? (
									<ul className="supplier-contracts-rates-references">
										{referencedEntities.map((entity, index) => (
											<li key={`${entity}-${index}`}>{entity}</li>
										))}
									</ul>
								) : (
									<span className="supplier-contracts-rates-muted">—</span>
								)}
							</td>
							<td>
								{classificationKey ? (
									<>
										{classificationKey}
										{typeof confidence === "number" && (
											<span className="supplier-contracts-rates-type-confidence">
												{` (${Math.round(confidence * 100)}%)`}
											</span>
										)}
									</>
								) : (
									<span className="supplier-contracts-rates-muted">—</span>
								)}
							</td>
							<td>
								{validity || <span className="supplier-contracts-rates-muted">—</span>}
							</td>
							<td>
								<Badge
									label={statusLabel(document.status)}
									type={statusBadgeType(document.status)}
								/>
							</td>
							<td className="is-button">
								<div className="supplier-contracts-rates-row-actions">
									<Button
										xsSmall={true}
										label="Process"
										onClick={() => setProcessingDoc(document)}
									/>
									<Button
										xsSmall={true}
										label="View"
										onClick={() => setSelected(document)}
									/>
									{user?.is_admin && (
										<Button
											xsSmall={true}
											icon={<Trash2 size={14} />}
											onClick={() => confirmDelete(document)}
										/>
									)}
								</div>
							</td>
						</tr>
					)
				})}
			</Table>

			{selected && (
				<DocumentDetail
					document={selected}
					user={user}
					close={() => setSelected(null)}
				/>
			)}

			{processingDoc && (
				<ProcessModal
					doc={documents.find((entry) => entry.id === processingDoc.id) || processingDoc}
					onClose={() => setProcessingDoc(null)}
				/>
			)}
		</div>
	)
}

function DocumentDetail({ document, user, close }) {
	const [body, setBody] = useState(null)
	const [bodyError, setBodyError] = useState(null)
	const [bodyLoading, setBodyLoading] = useState(true)
	const [inlineImageUrls, setInlineImageUrls] = useState({})

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			if (!document?.body_storage_path) {
				setBody("")
				setBodyLoading(false)
				return
			}

			try {
				const html = await loadCommunicationBody(document.body_storage_path)
				if (cancelled) return
				setBody(html || "")
				setBodyLoading(false)
			} catch (err) {
				if (cancelled) return
				console.error("Failed to load supplier document body", err)
				setBodyError(err?.message || "Failed to load body")
				setBodyLoading(false)
			}
		}

		load()

		return () => {
			cancelled = true
		}
	}, [document?.body_storage_path])

	useEffect(() => {
		let cancelled = false

		const loadInlineImages = async () => {
			const inlineImages = (document?.attachments || []).filter(
				(attachment) => attachment.is_inline && attachment.storage_path
			)

			if (inlineImages.length === 0) {
				setInlineImageUrls({})
				return
			}

			const next = {}
			await Promise.all(
				inlineImages.map(async (attachment) => {
					try {
						const url =
							attachment.download_url ||
							(await getCommunicationBodyUrl(attachment.storage_path))
						for (const key of inlineImageLookupKeys(attachment)) {
							next[key] = url
						}
					} catch (err) {
						console.error("Failed to load supplier inline image URL", err)
					}
				})
			)

			if (!cancelled) setInlineImageUrls(next)
		}

		loadInlineImages()

		return () => {
			cancelled = true
		}
	}, [document?.attachments])

	const renderedBody = useMemo(
		() => replaceCidImageSources(body, inlineImageUrls),
		[body, inlineImageUrls]
	)

	const approve = async () => {
		await db.doc(`supplier_contract_documents/${document.id}`).update({
			status: "APPROVED",
			reviewed_by: user?.uid || null,
			reviewed_at: firebase.firestore.FieldValue.serverTimestamp(),
			updated_at: firebase.firestore.FieldValue.serverTimestamp(),
		})
		close()
	}

	const reopen = async () => {
		await db.doc(`supplier_contract_documents/${document.id}`).update({
			status: "REVIEW_REQUIRED",
			reviewed_by: null,
			reviewed_at: null,
			updated_at: firebase.firestore.FieldValue.serverTimestamp(),
		})
		close()
	}

	const parsed = document.parsed
	const supplier = document.supplier_details
	const meta = document.document_metadata
	const agent = document.agent_metadata
	const validity = formatValidity(document.effective_from, document.effective_to)
	const contacts = supplier?.contacts || []
	const isEmail = document.source === "EMAIL_FORWARD"
	const receivedAt = document.email_received_at?.seconds
		? moment.unix(document.email_received_at.seconds).format("MMM D[,] YYYY [at] HH:mm")
		: null
	const ingestedAt = document.created_at?.seconds
		? moment.unix(document.created_at.seconds).format("MMM D[,] YYYY [at] HH:mm")
		: null
	const parsedAt = document.parsed_at?.seconds
		? moment.unix(document.parsed_at.seconds).format("MMM D[,] YYYY [at] HH:mm")
		: null

	return (
		<Window
			title={document.email_subject || document.filename || "Supplier document"}
			className="supplier-doc-drawer"
			close={() => close()}>
			<div className="supplier-doc-drawer-body">
				<div className="supplier-doc-drawer-meta">
					<MetaRow
						label="Document ID"
						value={<code>{document.id}</code>}
					/>
					{isEmail ? (
						<>
							<MetaRow
								label="From"
								value={document.email_from || <small className="no-value">—</small>}
							/>
							<MetaRow
								label="To"
								value={
									document.email_to?.length > 0
										? document.email_to.join(", ")
										: <small className="no-value">—</small>
								}
							/>
							<MetaRow
								label="Received"
								value={receivedAt || <small className="no-value">—</small>}
							/>
						</>
					) : (
						<MetaRow
							label="Filename"
							value={
								document.filename ? (
									<>
										<strong>{document.filename}</strong>
										{(document.content_type || formatFileSize(document.size)) && (
											<small className="supplier-doc-drawer-confidence">
												({[document.content_type, formatFileSize(document.size)]
													.filter(Boolean)
													.join(" · ")})
											</small>
										)}
									</>
								) : (
									<small className="no-value">—</small>
								)
							}
						/>
					)}
					<MetaRow
						label="Status"
						value={
							<>
								<Badge
									label={statusLabel(document.status)}
									type={statusBadgeType(document.status)}
								/>
								<small className="supplier-doc-drawer-confidence">
									({isEmail ? "Email forward" : "Manual upload"}
									{ingestedAt && ` · ${ingestedAt}`})
								</small>
							</>
						}
					/>
					<MetaRow
						label="Last parsed"
						value={
							parsedAt ? (
								<>
									{parsedAt}
									{document.parsed_with_model && (
										<small className="supplier-doc-drawer-confidence">
											({document.parsed_with_model})
										</small>
									)}
								</>
							) : (
								<small className="no-value">Not parsed yet</small>
							)
						}
					/>
					<MetaRow
						label="Classification"
						value={
							document.classification ? (
								<>
									<strong>{document.classification}</strong>
									{typeof agent?.confidence_score === "number" && (
										<small className="supplier-doc-drawer-confidence">
											({Math.round(agent.confidence_score * 100)}% confidence)
										</small>
									)}
								</>
							) : (
								<small className="no-value">Not classified</small>
							)
						}
					/>
					{supplier?.formatted_name && (
						<MetaRow
							label="Supplier"
							value={
								<>
									<strong>{supplier.formatted_name}</strong>
									{supplier.legal_entity && (
										<small className="supplier-doc-drawer-confidence">
											({supplier.legal_entity})
										</small>
									)}
									{supplier.original_name &&
										supplier.original_name !== supplier.formatted_name && (
											<small className="supplier-doc-drawer-confidence">
												{supplier.original_name}
											</small>
										)}
								</>
							}
						/>
					)}
					{document.referenced_hotels?.length > 0 && (
						<MetaRow
							label="Referenced hotels"
							value={document.referenced_hotels.join(", ")}
						/>
					)}
					{document.referenced_courses?.length > 0 && (
						<MetaRow
							label="Referenced courses"
							value={document.referenced_courses.join(", ")}
						/>
					)}
					{document.supplier_reference && (
						<MetaRow
							label="Reference"
							value={<code>{document.supplier_reference}</code>}
						/>
					)}
					{validity && <MetaRow label="Validity" value={validity} />}
					{contacts.length > 0 && (
						<MetaRow
							label="Contacts"
							value={
								<ul className="supplier-doc-drawer-contacts">
									{contacts.map((contact, index) => (
										<li key={index}>
											<strong>{contact.name || "Unnamed contact"}</strong>
											{contact.email && (
												<> · <a href={`mailto:${contact.email}`}>{contact.email}</a></>
											)}
											{contact.phone && <> · {contact.phone}</>}
										</li>
									))}
								</ul>
							}
						/>
					)}
				</div>

				{document.internet_message_id && (
					<div className="supplier-doc-drawer-ids">
						<small>
							<strong>Message ID:</strong> {document.internet_message_id}
						</small>
					</div>
				)}

				{(document.classification || document.processing_steps) && (
					<div className="supplier-doc-drawer-section">
						<h3>Processing</h3>
						<Timeline
							data={buildSupplierTimeline(document, { phase: "done", now: Date.now() })}
						/>
					</div>
				)}

				{document.attachments?.some((attachment) => !attachment.is_inline) && (
					<div className="supplier-doc-drawer-section">
						<h3>Attachments</h3>
						<SupplierAttachmentsList attachments={document.attachments} />
					</div>
				)}

				<div className="supplier-doc-drawer-section">
					<details>
						<summary className="supplier-doc-drawer-raw-summary">
							Body
						</summary>
						<SupplierBodyPreview
							document={document}
							body={renderedBody}
							bodyLoading={bodyLoading}
							bodyError={bodyError}
						/>
					</details>
				</div>

				{agent?.reasoning && (
					<div className="supplier-doc-drawer-section">
						<details>
							<summary className="supplier-doc-drawer-raw-summary">
								Agent reasoning
							</summary>
							<span className="supplier-doc-drawer-reasoning">
								{agent.reasoning}
							</span>
						</details>
					</div>
				)}

				{document.notes && (
					<div className="supplier-doc-drawer-section">
						<details>
							<summary className="supplier-doc-drawer-raw-summary">
								Notes
							</summary>
							<span className="supplier-doc-drawer-reasoning">
								{document.notes}
							</span>
						</details>
					</div>
				)}

				{parsed && (
					<div className="supplier-doc-drawer-section">
						<details>
							<summary className="supplier-doc-drawer-raw-summary">
								Raw parser JSON
							</summary>
							<pre className="ai-panel-result-json">
								{JSON.stringify(parsed, null, 2)}
							</pre>
						</details>
					</div>
				)}

				<div className="supplier-contracts-rates-detail-actions">
					{document.status !== "APPROVED" && (
						<Button
							label="Approve AI result"
							onClick={() => approve()}
						/>
					)}
					{document.status === "APPROVED" && (
						<Button
							label="Reopen review"
							onClick={() => reopen()}
						/>
					)}
				</div>
			</div>
		</Window>
	)
}

function MetaRow({ label, value }) {
	return (
		<div className="supplier-doc-drawer-meta-row">
			<span className="supplier-doc-drawer-meta-label">{label}</span>
			<span className="supplier-doc-drawer-meta-value">{value}</span>
		</div>
	)
}

function SupplierBodyPreview({ document, body, bodyLoading, bodyError }) {
	const contentType = document.content_type
	const downloadUrl = document.download_url
	const hasHtmlBody = !!document.body_storage_path

	if (hasHtmlBody) {
		if (bodyLoading) {
			return (
				<div className="supplier-doc-drawer-body-loading">
					<div className="svg-loading-wrap">
						<LoadingIcon />
					</div>
					<small>Loading body...</small>
				</div>
			)
		}
		if (bodyError) {
			return (
				<div className="supplier-doc-drawer-body-error">
					<p>{bodyError}</p>
					<small>
						Check that you have <code>is_admin</code> on your user doc and that Storage rules
						are deployed.
					</small>
				</div>
			)
		}
		if (!body) {
			return (
				<div className="supplier-doc-drawer-body-empty">
					<small>This document has no body content.</small>
				</div>
			)
		}
		return (
			<iframe
				className="supplier-doc-drawer-iframe"
				sandbox=""
				srcDoc={body}
				title={document.email_subject || document.filename || "Document body"}
			/>
		)
	}

	if (downloadUrl && contentType?.startsWith("image/")) {
		return (
			<a
				href={downloadUrl}
				target="_blank"
				rel="noreferrer"
				className="supplier-doc-drawer-image-preview">
				<img
					src={downloadUrl}
					alt={document.filename || "Document preview"}
				/>
			</a>
		)
	}

	if (downloadUrl && contentType === "application/pdf") {
		return (
			<iframe
				className="supplier-doc-drawer-iframe supplier-doc-drawer-iframe-file"
				src={downloadUrl}
				title={document.filename || "Document preview"}
			/>
		)
	}

	if (downloadUrl) {
		return (
			<div className="supplier-doc-drawer-body-empty">
				<small>
					No inline preview for this file type.{" "}
					<a href={downloadUrl} target="_blank" rel="noreferrer">
						Open original
					</a>
				</small>
			</div>
		)
	}

	return (
		<div className="supplier-doc-drawer-body-empty">
			<small>No body or file available for this document.</small>
		</div>
	)
}

function SupplierAttachmentsList({ attachments = [] }) {
	const files = useMemo(
		() => attachments.filter((attachment) => !attachment.is_inline),
		[attachments]
	)
	const [urls, setUrls] = useState({})
	const [failedUrls, setFailedUrls] = useState({})

	useEffect(() => {
		let cancelled = false

		const loadUrls = async () => {
			const nextUrls = {}
			const nextFailed = {}

			await Promise.all(
				files.map(async (attachment) => {
					if (!attachment.storage_path) return

					try {
						nextUrls[attachment.storage_path] =
							attachment.download_url ||
							(await getCommunicationBodyUrl(attachment.storage_path))
					} catch (err) {
						console.error("Failed to load supplier attachment URL", err)
						nextFailed[attachment.storage_path] = true
					}
				})
			)

			if (cancelled) return
			setUrls(nextUrls)
			setFailedUrls(nextFailed)
		}

		loadUrls()

		return () => {
			cancelled = true
		}
	}, [files])

	if (files.length === 0) return null

	return (
		<div className="supplier-doc-drawer-attachments">
			{files.map((attachment) => {
				const url = urls[attachment.storage_path]
				const isImage = attachment.content_type?.startsWith("image/")

				return (
					<div
						key={
							attachment.storage_path || attachment.content_id || attachment.name
						}
						className={`supplier-doc-drawer-attachment ${isImage ? "is-image" : ""}`}>
						{isImage && url && (
							<a
								href={url}
								target="_blank"
								rel="noreferrer"
								className="supplier-doc-drawer-attachment-preview">
								<img
									src={url}
									alt={attachment.name || "Attachment preview"}
								/>
							</a>
						)}
						<div className="supplier-doc-drawer-attachment-details">
							{url ? (
								<a href={url} target="_blank" rel="noreferrer">
									{attachment.name || "Attachment"}
								</a>
							) : (
								<strong>{attachment.name || "Attachment"}</strong>
							)}
							<small>
								{attachment.content_type || "unknown type"}
								{typeof attachment.size === "number"
									? ` · ${formatFileSize(attachment.size)}`
									: ""}
								{failedUrls[attachment.storage_path] ? " · preview unavailable" : ""}
							</small>
							{attachment.storage_path && (
								<code className="supplier-doc-drawer-attachment-path">
									{attachment.storage_path}
								</code>
							)}
						</div>
					</div>
				)
			})}
		</div>
	)
}

// CID image substitution helpers — mirror the equivalents in the
// communications email-drawer. Supplier emails include inline images by
// content-id reference (cid:foo@bar) which won't render unless we swap them
// for signed Storage URLs before injecting the HTML into a sandboxed iframe.
function replaceCidImageSources(html, inlineImageUrls) {
	if (!html || !inlineImageUrls || Object.keys(inlineImageUrls).length === 0) return html

	return html.replace(
		/(src\s*=\s*)(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi,
		(match, prefix, doubleQuoted, singleQuoted, unquoted) => {
			const rawValue = doubleQuoted ?? singleQuoted ?? unquoted ?? ""
			if (!rawValue.toLowerCase().startsWith("cid:")) return match

			const url = findInlineImageUrl(rawValue, inlineImageUrls)
			if (!url) return match
			const quote =
				doubleQuoted !== undefined ? `"` : singleQuoted !== undefined ? `'` : `"`
			return `${prefix}${quote}${escapeHtmlAttribute(url)}${quote}`
		}
	)
}

function findInlineImageUrl(cidValue, inlineImageUrls) {
	const normalised = normaliseContentId(cidValue)
	if (inlineImageUrls[normalised]) return inlineImageUrls[normalised]

	const filenamePart = normalised.split("@")[0]
	if (inlineImageUrls[filenamePart]) return inlineImageUrls[filenamePart]

	return null
}

function inlineImageLookupKeys(attachment) {
	return [attachment.content_id, attachment.name]
		.map(normaliseContentId)
		.filter(Boolean)
}

function normaliseContentId(contentId) {
	if (!contentId) return ""
	let next = String(contentId)
		.trim()
		.replace(/^cid:/i, "")
		.replace(/^<|>$/g, "")
	try {
		next = decodeURIComponent(next)
	} catch {
		// Content IDs are often plain strings; malformed URI sequences can be
		// compared as-is.
	}
	return next.toLowerCase()
}

function escapeHtmlAttribute(value) {
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
}
